/**
 * Settings — Tenant profil + KV ayarlar.
 *
 * Tenant profili: legal_name, tax_id, brand_color, IBAN vs.
 * QR Menü: logo + arka plan görseli (multipart upload) + dil/sipariş
 * ayarları için Sistem Ayarları'na yönlendirme.
 */
import { Card, Stack, Switch, Text, Title, TextInput, Group, Button, Tabs, ColorInput, Loader, Center, Divider, FileInput, Image, NumberInput, Select, MultiSelect } from "@mantine/core";
import { IconDeviceFloppy, IconPhoto } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useEffect, useState } from "react";
import { useAuth } from "../../../shared/auth";
import { apiClient } from "../../../shared/api";
import { PageHeader } from "../../../shared/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// --- KV ayar oku/yaz (tek key) — Sistem Ayarları ile aynı endpoint ---
function useSettingValue<T>(key: string) {
  return useQuery({
    queryKey: ["setting", key],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/settings/${key}/value/`)).data.value as T,
  });
}

function useSettingMutation(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (value: any) =>
      (await apiClient.put(`/api/v1/settings/${key}/value/`, { value })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["setting", key] });
      qc.invalidateQueries({ queryKey: ["settings-registry"] });
    },
    onError: (e: any) =>
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" }),
  });
}

// Tenant PATCH — bir görsel alanı (logo/arka plan) staged File ise multipart,
// değilse JSON. Dokunulmamış URL string alanları gönderilmez (ezilmesin).
const TENANT_IMAGE_FIELDS = ["logo", "menu_background_image"];

async function patchTenant(payload: Record<string, any>) {
  const hasNewFile = TENANT_IMAGE_FIELDS.some((f) => payload[f] instanceof File);
  if (!hasNewFile) {
    // Görsel alanlarını JSON yolundan çıkar (string URL'i geri yazmak anlamsız).
    const json: Record<string, any> = { ...payload };
    for (const f of TENANT_IMAGE_FIELDS) {
      if (!(json[f] instanceof File)) delete json[f];
    }
    return (await apiClient.patch("/api/v1/tenants/me/", json)).data;
  }
  const fd = new FormData();
  for (const [k, v] of Object.entries(payload)) {
    if (TENANT_IMAGE_FIELDS.includes(k)) {
      if (v instanceof File) fd.append(k, v);
      else if (v === null) fd.append(k, "");  // açık temizleme
      // string URL → değişmedi, gönderme
      continue;
    }
    if (v === null || v === undefined) continue;
    if (typeof v === "boolean") fd.append(k, v ? "true" : "false");
    else fd.append(k, String(v));
  }
  return (
    await apiClient.patch("/api/v1/tenants/me/", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    })
  ).data;
}

export function SettingsPage() {
  const { tenant } = useAuth();

  return (
    <Stack gap="lg">
      <PageHeader title="Ayarlar" subtitle="Restoran profili ve sistem ayarları" />

      <Tabs defaultValue="profile">
        <Tabs.List>
          <Tabs.Tab value="profile">Restoran Profili</Tabs.Tab>
          <Tabs.Tab value="qr">QR Menü</Tabs.Tab>
          <Tabs.Tab value="financial">Mali / Vergi</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="profile" pt="md">
          <TenantProfileForm />
        </Tabs.Panel>

        <Tabs.Panel value="qr" pt="md">
          <QrBrandingForm />
        </Tabs.Panel>

        <Tabs.Panel value="financial" pt="md">
          <Card padding="lg" radius="md" withBorder>
            <Title order={4} mb="md">Mevcut Tenant</Title>
            <Text><strong>Ad:</strong> {tenant?.name}</Text>
            <Text><strong>Slug:</strong> {tenant?.slug}</Text>
            <Text c="dimmed" size="sm" mt="md">
              Yasal bilgileri (VKN, mersis, IBAN) "Restoran Profili" sekmesinden düzenleyin.
            </Text>
          </Card>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function TenantProfileForm() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["tenant-me"],
    queryFn: async () => (await apiClient.get("/api/v1/tenants/me/")).data,
  });
  const [form, setForm] = useState<Record<string, any>>({});

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const mutation = useMutation({
    mutationFn: patchTenant,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant-me"] });
      notifications.show({ message: "Profil güncellendi", color: "green" });
    },
    onError: (e: any) => notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" }),
  });

  if (isLoading) return <Center h={200}><Loader /></Center>;

  return (
    <Card padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <Title order={4}>Genel Bilgi</Title>
        <TextInput label="Marka Adı" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.currentTarget.value })} />
        <TextInput label="Yasal Ad" value={form.legal_name || ""} onChange={(e) => setForm({ ...form, legal_name: e.currentTarget.value })} />
        <ColorInput label="Brand Color (CSS rengi)" value={form.brand_color || "#228be6"} onChange={(v) => setForm({ ...form, brand_color: v })} />

        <Divider my="sm" />
        <Title order={4}>Vergi / Yasal</Title>
        <Group grow>
          <TextInput label="VKN/TCKN" value={form.tax_id || ""} onChange={(e) => setForm({ ...form, tax_id: e.currentTarget.value })} />
          <TextInput label="Vergi Dairesi" value={form.tax_office || ""} onChange={(e) => setForm({ ...form, tax_office: e.currentTarget.value })} />
        </Group>
        <Group grow>
          <TextInput label="MERSIS No" value={form.mersis_no || ""} onChange={(e) => setForm({ ...form, mersis_no: e.currentTarget.value })} />
          <TextInput label="Sicil No" value={form.trade_registry_no || ""} onChange={(e) => setForm({ ...form, trade_registry_no: e.currentTarget.value })} />
        </Group>

        <Divider my="sm" />
        <Title order={4}>Adres</Title>
        <Group grow>
          <TextInput label="İlçe" value={form.district || ""} onChange={(e) => setForm({ ...form, district: e.currentTarget.value })} />
          <TextInput label="Şehir" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.currentTarget.value })} />
        </Group>
        <Group grow>
          <TextInput label="Posta Kodu" value={form.postal_code || ""} onChange={(e) => setForm({ ...form, postal_code: e.currentTarget.value })} />
          <TextInput label="Ülke" value={form.country || "TR"} onChange={(e) => setForm({ ...form, country: e.currentTarget.value })} />
        </Group>

        <Divider my="sm" />
        <Title order={4}>İletişim</Title>
        <Group grow>
          <TextInput label="Telefon" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.currentTarget.value })} />
          <TextInput label="E-posta" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.currentTarget.value })} />
        </Group>
        <TextInput label="Web Sitesi" value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.currentTarget.value })} />

        <Divider my="sm" />
        <Title order={4}>Banka</Title>
        <Group grow>
          <TextInput label="Banka Adı" value={form.bank_name || ""} onChange={(e) => setForm({ ...form, bank_name: e.currentTarget.value })} />
          <TextInput label="IBAN" value={form.iban || ""} onChange={(e) => setForm({ ...form, iban: e.currentTarget.value })} />
        </Group>

        <Group justify="flex-end" mt="md">
          <Button leftSection={<IconDeviceFloppy size={16} />} onClick={() => mutation.mutate(form)} loading={mutation.isPending}>
            Kaydet
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/**
 * QR Menü markalama — logo + landing arka plan görseli (multipart upload).
 * Dil/sipariş/ödeme ayarları KV store'da; Sistem Ayarları > QR grubundan
 * yönetilir (burada kısa bir yönlendirme notu var).
 */
function QrBrandingForm() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["tenant-me"],
    queryFn: async () => (await apiClient.get("/api/v1/tenants/me/")).data,
  });
  // Staged dosyalar (File) veya null (temizleme); değişmediyse undefined kalır.
  const [logo, setLogo] = useState<File | null | undefined>(undefined);
  const [bg, setBg] = useState<File | null | undefined>(undefined);

  const mutation = useMutation({
    mutationFn: patchTenant,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant-me"] });
      notifications.show({ message: "QR menü markası güncellendi", color: "green" });
      setLogo(undefined);
      setBg(undefined);
    },
    onError: (e: any) => notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" }),
  });

  if (isLoading) return <Center h={200}><Loader /></Center>;

  function save() {
    const payload: Record<string, any> = {};
    if (logo !== undefined) payload.logo = logo;       // File veya null
    if (bg !== undefined) payload.menu_background_image = bg;
    if (Object.keys(payload).length === 0) {
      notifications.show({ message: "Değişiklik yok", color: "blue" });
      return;
    }
    mutation.mutate(payload);
  }

  // Önizleme: staged File varsa objectURL, yoksa mevcut sunucu URL'i.
  const logoPreview = logo instanceof File ? URL.createObjectURL(logo) : (logo === null ? null : data?.logo);
  const bgPreview = bg instanceof File ? URL.createObjectURL(bg) : (bg === null ? null : data?.menu_background_image);

  return (
    <Stack gap="md">
      <Card padding="lg" radius="md" withBorder>
        <Stack gap="md">
          <Title order={4}>Marka Görselleri</Title>
          <Text size="sm" c="dimmed">
            QR menü açılış (landing) ekranında gösterilir — logo üstte, arka plan
            görseli tüm ekranı kaplar (üzerine okunabilirlik için koyu katman biner).
          </Text>

          <Group align="flex-start" grow>
            <Stack gap={6}>
              <FileInput
                label="Logo"
                placeholder="Görsel seç (PNG/JPG)"
                accept="image/*"
                leftSection={<IconPhoto size={16} />}
                value={logo instanceof File ? logo : null}
                onChange={(f) => setLogo(f)}
                clearable
              />
              {logoPreview ? (
                <Image
                  src={logoPreview}
                  h={80}
                  w={120}
                  fit="contain"
                  radius="sm"
                  bg="gray.1"
                />
              ) : (
                <Text size="xs" c="dimmed">Henüz logo yok</Text>
              )}
              {data?.logo && (
                <Button size="compact-xs" variant="subtle" color="red" onClick={() => setLogo(null)}>
                  Logoyu kaldır
                </Button>
              )}
            </Stack>

            <Stack gap={6}>
              <FileInput
                label="Arka Plan Görseli"
                placeholder="Görsel seç (PNG/JPG)"
                accept="image/*"
                leftSection={<IconPhoto size={16} />}
                value={bg instanceof File ? bg : null}
                onChange={(f) => setBg(f)}
                clearable
              />
              {bgPreview ? (
                <Image
                  src={bgPreview}
                  h={80}
                  w={140}
                  fit="cover"
                  radius="sm"
                  bg="gray.1"
                />
              ) : (
                <Text size="xs" c="dimmed">Henüz arka plan yok</Text>
              )}
              {data?.menu_background_image && (
                <Button size="compact-xs" variant="subtle" color="red" onClick={() => setBg(null)}>
                  Arka planı kaldır
                </Button>
              )}
            </Stack>
          </Group>

          <Group justify="flex-end">
            <Button leftSection={<IconDeviceFloppy size={16} />} onClick={save} loading={mutation.isPending}>
              Kaydet
            </Button>
          </Group>
        </Stack>
      </Card>

      <QrAppearanceCard />

      <QrOrderLangCard />

      <Card padding="lg" radius="md" withBorder>
        <Stack gap={4}>
          <Title order={5}>Çeviriler</Title>
          <Text size="sm" c="dimmed">
            Ürün/kategori adlarının İngilizce çevirisi ilgili ürün/kategori
            düzenleme ekranındaki çeviri (EN) editöründen girilir.
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}

/**
 * QR sipariş & dil ayarları — canlı KV oku/yaz (Sistem Ayarları'ndan taşındı,
 * artık tek yer burası).
 */
function QrOrderLangCard() {
  const orderEnabled = useSettingValue<boolean>("qr_order_enabled");
  const waiterApproval = useSettingValue<boolean>("qr_require_waiter_approval");
  const showPrices = useSettingValue<boolean>("qr_show_prices");
  const paymentMode = useSettingValue<string>("qr_payment_mode");
  const defaultLang = useSettingValue<string>("qr_default_lang");
  const langsEnabled = useSettingValue<string[]>("qr_languages_enabled");

  const setOrderEnabled = useSettingMutation("qr_order_enabled");
  const setWaiterApproval = useSettingMutation("qr_require_waiter_approval");
  const setShowPrices = useSettingMutation("qr_show_prices");
  const setPaymentMode = useSettingMutation("qr_payment_mode");
  const setDefaultLang = useSettingMutation("qr_default_lang");
  const setLangsEnabled = useSettingMutation("qr_languages_enabled");

  return (
    <Card padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <Title order={5}>Sipariş & Dil</Title>

        <Switch
          label="QR menüden sipariş alma açık"
          description="Kapalıyken QR menüsü sadece görüntüleme amaçlı kalır."
          checked={orderEnabled.data !== false}
          onChange={(e) => setOrderEnabled.mutate(e.currentTarget.checked)}
        />
        <Switch
          label="QR siparişinde garson onayı gerekli"
          description="Kapalıyken müşteri siparişi doğrudan mutfağa düşer."
          checked={waiterApproval.data !== false}
          onChange={(e) => setWaiterApproval.mutate(e.currentTarget.checked)}
        />
        <Switch
          label="QR menüde fiyat göster"
          checked={showPrices.data !== false}
          onChange={(e) => setShowPrices.mutate(e.currentTarget.checked)}
        />

        <Select
          label="Ödeme modu"
          description="Masada müşteri ödeme şekli."
          data={[
            { value: "NONE", label: "Yok (sadece sipariş)" },
            { value: "CALL_WAITER", label: "Garson çağır" },
            { value: "DIGITAL", label: "Dijital ödeme" },
          ]}
          value={paymentMode.data || "CALL_WAITER"}
          onChange={(v) => v && setPaymentMode.mutate(v)}
          w={260}
        />

        <Divider my="xs" />

        <Group grow>
          <Select
            label="Varsayılan dil"
            description="?lang verilmezse gelen ilk menü dili."
            data={[
              { value: "tr", label: "Türkçe" },
              { value: "en", label: "English" },
            ]}
            value={defaultLang.data || "tr"}
            onChange={(v) => v && setDefaultLang.mutate(v)}
          />
          <MultiSelect
            label="Sunulan diller"
            description="Müşteriye dil değiştiricide gösterilecek diller."
            data={[
              { value: "tr", label: "Türkçe" },
              { value: "en", label: "English" },
            ]}
            value={langsEnabled.data || ["tr", "en"]}
            onChange={(v) => setLangsEnabled.mutate(v)}
          />
        </Group>
      </Stack>
    </Card>
  );
}

/**
 * QR açılış (landing) görünüm ayarları — canlı KV oku/yaz.
 * - Logo varken adı gizle
 * - Buzlu cam (blur) efekti + saydamlık %
 */
function QrAppearanceCard() {
  const hideName = useSettingValue<boolean>("qr_hide_name_when_logo");
  const blur = useSettingValue<boolean>("qr_card_blur");
  const opacity = useSettingValue<number>("qr_card_opacity");
  const showReservation = useSettingValue<boolean>("qr_show_reservation");
  const logoTint = useSettingValue<boolean>("qr_logo_tint_enabled");
  const logoTintColor = useSettingValue<string>("qr_logo_tint_color");
  const setHideName = useSettingMutation("qr_hide_name_when_logo");
  const setBlur = useSettingMutation("qr_card_blur");
  const setOpacity = useSettingMutation("qr_card_opacity");
  const setShowReservation = useSettingMutation("qr_show_reservation");
  const setLogoTint = useSettingMutation("qr_logo_tint_enabled");
  const setLogoTintColor = useSettingMutation("qr_logo_tint_color");
  const accentColor = useSettingValue<string>("qr_accent_color");
  const setAccentColor = useSettingMutation("qr_accent_color");

  // Saydamlık input'u — yazarken anlık state, kaydet butonuyla PUT.
  const [opacityDraft, setOpacityDraft] = useState<number | "">("");
  useEffect(() => {
    if (typeof opacity.data === "number") setOpacityDraft(opacity.data);
  }, [opacity.data]);

  // Logo rengi — yazarken anlık state, kaydet ile PUT (her tuşta istek atma).
  const [tintDraft, setTintDraft] = useState("#ffffff");
  useEffect(() => {
    if (typeof logoTintColor.data === "string") setTintDraft(logoTintColor.data);
  }, [logoTintColor.data]);

  // Vurgu rengi — boşsa QR menü marka rengini kullanır.
  const [accentDraft, setAccentDraft] = useState("");
  useEffect(() => {
    if (typeof accentColor.data === "string") setAccentDraft(accentColor.data);
  }, [accentColor.data]);

  const blurOn = !!blur.data;

  return (
    <Card padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <Title order={5}>Açılış Görünümü</Title>

        <Switch
          label="Logo varken restoran adını gizle"
          description="Logo yüklüyse açılış ekranında restoran adı yazısı gösterilmez (çift marka önlenir)."
          checked={!!hideName.data}
          onChange={(e) => setHideName.mutate(e.currentTarget.checked)}
        />

        <Switch
          label="Rezervasyon butonunu göster"
          description="Açılış ekranındaki 'Rezervasyon Yap' butonu. Kapalıyken sadece 'Menüyü Aç' gösterilir."
          checked={showReservation.data !== false}
          onChange={(e) => setShowReservation.mutate(e.currentTarget.checked)}
        />

        <Divider my="xs" />

        {/* Logo renklendirme — tek renk + şeffaf zeminli logoyu istenen renge boyar */}
        <Switch
          label="Logoyu renklendir"
          description="Logoyu seçilen renge boyar. Yalnızca tek renk + şeffaf zeminli (silhouette) logoda düzgün çalışır; çok renkli/fotoğraf logoda kapalı tutun."
          checked={!!logoTint.data}
          onChange={(e) => setLogoTint.mutate(e.currentTarget.checked)}
        />
        <Group align="flex-end" gap="sm">
          <ColorInput
            label="Logo rengi"
            disabled={!logoTint.data}
            value={tintDraft}
            onChange={setTintDraft}
            format="hex"
            w={220}
          />
          <Button
            variant="light"
            leftSection={<IconDeviceFloppy size={16} />}
            disabled={!logoTint.data}
            loading={setLogoTintColor.isPending}
            onClick={() => {
              setLogoTintColor.mutate(tintDraft);
              notifications.show({ message: "Logo rengi kaydedildi", color: "green" });
            }}
          >
            Kaydet
          </Button>
        </Group>

        <Divider my="xs" />

        {/* Vurgu rengi — fiyatlar ve sipariş butonları (boşsa marka rengi) */}
        <Group align="flex-end" gap="sm">
          <ColorInput
            label="Vurgu rengi (fiyat ve sipariş butonları)"
            description="Boş bırakılırsa marka rengi kullanılır."
            placeholder="Marka rengi"
            value={accentDraft}
            onChange={setAccentDraft}
            format="hex"
            w={260}
          />
          <Button
            variant="light"
            leftSection={<IconDeviceFloppy size={16} />}
            loading={setAccentColor.isPending}
            onClick={() => {
              setAccentColor.mutate(accentDraft);
              notifications.show({ message: "Vurgu rengi kaydedildi", color: "green" });
            }}
          >
            Kaydet
          </Button>
        </Group>

        <Divider my="xs" />

        <Switch
          label="Buzlu cam (blur) efekti"
          description="Açıkken kart yarı saydam olur ve arka plan görseli kartın arkasından bulanık görünür."
          checked={blurOn}
          onChange={(e) => setBlur.mutate(e.currentTarget.checked)}
        />

        {/* Saydamlık sadece blur açıkken anlamlı */}
        <Group align="flex-end" gap="sm">
          <NumberInput
            label="Kart yoğunluğu (%)"
            description="Saydamlık + bulanıklığı birlikte ayarlar. 0 = tam şeffaf, hiç blur yok (bg en net) · 100 = tam opak + tam blur."
            min={0}
            max={100}
            disabled={!blurOn}
            value={opacityDraft}
            onChange={(v) => setOpacityDraft(typeof v === "number" ? v : "")}
            w={260}
          />
          <Button
            variant="light"
            leftSection={<IconDeviceFloppy size={16} />}
            disabled={!blurOn || opacityDraft === ""}
            loading={setOpacity.isPending}
            onClick={() => {
              if (opacityDraft !== "") setOpacity.mutate(opacityDraft);
              notifications.show({ message: "Saydamlık kaydedildi", color: "green" });
            }}
          >
            Kaydet
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

